import React, { useEffect, useState } from 'react';
import { Tag, Button, Empty, Tooltip, Space, Progress, Divider } from 'antd';
import {
  ClockCircleOutlined, FireOutlined, ExclamationCircleOutlined,
  CheckCircleOutlined, MailOutlined, ReloadOutlined,
  SafetyOutlined, CalendarOutlined, AuditOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../../variables/api/axiosInstance';
import { formatDate, formatMontant } from '../../../variables/constants';
import RelanceModal from '../../relances/RelanceModal';
import KpiCards from '../../../components/KpiCards';
import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../../i18n/libelles';
import { libelleDelai } from '../../../i18n/donnees';

const NIVEAUX = avecLibelleTraduit({
  CRITIQUE: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: <FireOutlined /> },
  URGENT: { color: '#ea580c', bg: '#ffedd5', border: '#fdba74', icon: <ExclamationCircleOutlined /> },
  ATTENTION: { color: '#a16207', bg: '#fef9c3', border: '#fde68a', icon: <ExclamationCircleOutlined /> },
  NORMAL: { color: '#15803d', bg: '#dcfce7', border: '#86efac', icon: <CheckCircleOutlined /> },
}, 'dossierSinistre:ongletDelais.niveaux');

const CATEGORIES = avecLibelleTraduit({
  GARANTIE: { color: '#0891b2' },
  RESPONSABILITE: { color: '#be185d' },
  EXPERTISE: { color: '#7c3aed' },
  OFFRE: { color: '#2563eb' },
  PAIEMENT: { color: '#059669' },
  RECOURS: { color: '#b91c1c' },
  PV: { color: '#9333ea' },
  PRESCRIPTION: { color: '#475569' },
  INTERNE: { color: '#1f2937' },
}, 'dossierSinistre:ongletDelais.categories');

export default function OngletDelaisPenalites({ sinistreTrackingId, onRelanceEnvoyee }) {
  const { t } = useTranslation('dossierSinistre');
  const [delais, setDelais] = useState([]);
  const [penalites, setPenalites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relanceTarget, setRelanceTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        axiosInstance.get(`/v1/delais/sinistre/${sinistreTrackingId}`).catch(() => ({ data: { data: [] } })),
        axiosInstance.get('/v1/delais/penalites').catch(() => ({ data: { data: [] } })),
      ]);
      setDelais(d.data?.data || []);
      // Filtrer les pénalités du sinistre courant côté front
      setPenalites((p.data?.data || []).filter(x => x.sinistreTrackingId === sinistreTrackingId));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (sinistreTrackingId) load(); }, [sinistreTrackingId]); // eslint-disable-line

  const initialiser = async () => {
    try {
      await axiosInstance.post(`/v1/delais/sinistre/${sinistreTrackingId}/initialiser`);
      load();
    } catch (e) { /* silencieux */ }
  };

  const totalPenalites = penalites.reduce((s, p) => s + Number(p.montantPenalite || 0), 0);
  const delaisActifs = delais.filter(d => d.statut !== 'RESOLU');
  const delaisCritiques = delaisActifs.filter(d => d.niveauAlerte === 'CRITIQUE').length;
  const delaisUrgents = delaisActifs.filter(d => d.niveauAlerte === 'URGENT').length;

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#6b7280' }}>{t('ongletDelais.chargement')}</div>;
  }

  return (
    <>
      {/* Bandeau résumé */}
      <KpiCards items={[
        { label: t('ongletDelais.kpi.critiques', { count: delaisCritiques }), value: delaisCritiques, color: '#dc2626' },
        { label: t('ongletDelais.kpi.urgents', { count: delaisUrgents }), value: delaisUrgents, color: '#ea580c' },
        { label: t('ongletDelais.kpi.actifs', { count: delaisActifs.length }), value: delaisActifs.length, color: '#15803d' },
        { label: t('ongletDelais.kpi.penalites'), value: formatMontant(totalPenalites), color: '#b91c1c' },
      ]} />

      {/* Section délais actifs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#111827' }}>
          <ClockCircleOutlined style={{ marginRight: 6, color: '#1a6b3c' }} />
          {t('ongletDelais.delaisEnCours')}
        </h3>
        <Space>
          {delaisActifs.length === 0 && (
            <Button size="small" onClick={initialiser}>{t('ongletDelais.initialiser')}</Button>
          )}
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>{t('ongletDelais.rafraichir')}</Button>
        </Space>
      </div>

      {delaisActifs.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                {t('ongletDelais.aucunDelai')}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                {t('ongletDelais.aucunDelaiAide')}
              </div>
            </div>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {delaisActifs
            .sort((a, b) => {
              const ordre = { CRITIQUE: 0, URGENT: 1, ATTENTION: 2, NORMAL: 3 };
              return (ordre[a.niveauAlerte] ?? 99) - (ordre[b.niveauAlerte] ?? 99);
            })
            .map(d => {
              const niv = NIVEAUX[d.niveauAlerte] || NIVEAUX.NORMAL;
              const cat = CATEGORIES[d.categorie] || {};
              const totalJours = (() => {
                if (!d.dateDebut || !d.dateEcheance) return null;
                const ms = new Date(d.dateEcheance) - new Date(d.dateDebut);
                return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
              })();
              const restantsAffiche = d.joursRestants <= 0
                ? t('ongletDelais.depasseDe', { n: Math.abs(d.joursRestants) })
                : t('ongletDelais.joursRestants', { n: d.joursRestants });
              const pctEcoule = totalJours
                ? Math.min(100, Math.max(0, ((totalJours - d.joursRestants) / totalJours) * 100))
                : 100;

              return (
                <div key={d.id} style={{
                  background: niv.bg + '40',
                  border: `1px solid ${niv.border}`,
                  borderRadius: 10,
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  flexWrap: 'wrap',
                }}>
                  <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <Tag color={niv.color === '#dc2626' ? 'error' : niv.color === '#ea580c' ? 'warning' : niv.color === '#a16207' ? 'gold' : 'success'}
                        style={{ borderRadius: 10, fontSize: 10, margin: 0 }}>
                        {niv.icon} {niv.label}
                      </Tag>
                      {cat.label && (
                        <Tag style={{ borderRadius: 10, fontSize: 10, color: cat.color, borderColor: cat.color, background: '#fff', margin: 0 }}>
                          {cat.label}
                        </Tag>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
                      {libelleDelai(d)}
                    </div>
                    {d.referenceJuridique && (
                      <Tooltip title={t('ongletDelais.articleDelai')}>
                        <span style={{
                          display: 'inline-block',
                          fontSize: 10, fontFamily: 'ui-monospace, monospace',
                          padding: '1px 6px', background: '#fef3c7',
                          border: '1px solid #fde68a', borderRadius: 4,
                          color: '#78350f',
                        }}>
                          <AuditOutlined style={{ marginRight: 4 }} />{d.referenceJuridique}
                        </span>
                      </Tooltip>
                    )}
                  </div>

                  <div style={{ flex: '0 0 200px' }}>
                    <Progress percent={Math.round(pctEcoule)} showInfo={false} size="small"
                      strokeColor={niv.color} trailColor="#e5e7eb" />
                    <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
                      <span><CalendarOutlined style={{ marginRight: 4 }} />{formatDate(d.dateEcheance)}</span>
                      <span style={{ color: niv.color, fontWeight: 700 }}>{restantsAffiche}</span>
                    </div>
                  </div>

                  <Space size={4}>
                    {d.nombreAlertes > 0 && (
                      <Tooltip
                        title={
                          d.derniereAlerteEnvoyee
                            ? t('ongletDelais.relancesEnvoyeesDerniere', { n: d.nombreAlertes, date: formatDate(d.derniereAlerteEnvoyee) })
                            : t('ongletDelais.relancesEnvoyees', { n: d.nombreAlertes })
                        }
                      >
                        <Tag style={{ borderRadius: 10, fontSize: 10, margin: 0, borderColor: '#bfdbfe', color: '#1e40af', background: '#eff6ff' }}>
                          <MailOutlined /> {d.nombreAlertes}
                          {d.derniereAlerteEnvoyee && (
                            <span style={{ fontWeight: 400, marginLeft: 4 }}>
                              · {formatDate(d.derniereAlerteEnvoyee)}
                            </span>
                          )}
                        </Tag>
                      </Tooltip>
                    )}
                    <Tooltip title={t('ongletDelais.envoyerRelance')}>
                      <Button size="small" type="primary" icon={<MailOutlined />}
                        style={{ background: niv.color, borderColor: niv.color }}
                        onClick={() => setRelanceTarget(d)}>
                        {t('ongletDelais.relancer')}
                      </Button>
                    </Tooltip>
                  </Space>
                </div>
              );
            })}
        </div>
      )}

      {/* Section pénalités */}
      <Divider style={{ margin: '16px 0 12px' }} />
      <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: '#111827' }}>
        <SafetyOutlined style={{ marginRight: 6, color: '#b91c1c' }} />
        {t('ongletDelais.penalitesCalculees')}
        {penalites.length > 0 && <Tag color="red" style={{ marginLeft: 8, borderRadius: 10 }}>{penalites.length}</Tag>}
      </h3>

      {penalites.length === 0 ? (
        <div style={{ padding: '20px', background: '#f9fafb', borderRadius: 10, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
          <CheckCircleOutlined style={{ marginRight: 6, color: '#15803d' }} />{t('ongletDelais.aucunePenalite')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {penalites.map(p => {
            const cat = CATEGORIES[p.categorie] || {};
            return (
              <div key={p.id} style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 10,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: '1 1 320px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {cat.label && (
                      <Tag style={{ borderRadius: 10, fontSize: 10, color: cat.color, borderColor: cat.color, background: '#fff', margin: 0 }}>
                        {cat.label}
                      </Tag>
                    )}
                    <span style={{ fontSize: 11, color: '#7f1d1d' }}>
                      {t('ongletDelais.calculeeLe', { date: formatDate(p.dateCalcul), mois: p.nombreMoisRetard, taux: p.tauxPctMois })}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
                    {libelleDelai(p)}
                  </div>
                  {p.referenceJuridique && (
                    <Tooltip title={t('ongletDelais.articlePenalite')}>
                      <span style={{
                        display: 'inline-block',
                        fontSize: 10, fontFamily: 'ui-monospace, monospace',
                        padding: '1px 6px', background: '#fef3c7',
                        border: '1px solid #fde68a', borderRadius: 4,
                        color: '#78350f',
                      }}>
                        <AuditOutlined style={{ marginRight: 4 }} />{p.referenceJuridique}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#7f1d1d' }}>
                    {t('ongletDelais.base', { montant: formatMontant(p.montantBase) })}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#b91c1c' }}>
                    {formatMontant(p.montantPenalite)}
                  </div>
                </div>
              </div>
            );
          })}
          <div style={{
            background: '#7f1d1d', color: '#fff',
            borderRadius: 10, padding: '10px 16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700,
          }}>
            <span>{t('ongletDelais.totalPenalites')}</span>
            <span style={{ fontSize: 18 }}>{formatMontant(totalPenalites)}</span>
          </div>
        </div>
      )}

      <RelanceModal
        notification={relanceTarget}
        open={!!relanceTarget}
        onClose={() => setRelanceTarget(null)}
        onSent={() => {
          setRelanceTarget(null);
          toast.success(t('ongletDelais.relanceEnvoyee'));
          load();
          // Fait apparaître l'envoi dans la frise sans recharger la page.
          onRelanceEnvoyee?.();
        }}
      />
    </>
  );
}
